/**
 * Browser-compatible drop-in replacement for Node's `querystring` module.
 * Uses the native URLSearchParams API (available in all modern browsers).
 *
 * Created during React 18 / Webpack 5 migration — Webpack 5 no longer
 * polyfills Node.js core modules like `querystring`.
 */

export type ParsedUrlQueryInput = Record<
  string,
  string | number | boolean | ReadonlyArray<string | number | boolean> | null | undefined
>;

/**
 * Parse a query string into a key-value object.
 * Compatible with Node's `querystring.parse()`.
 *
 * Note: URLSearchParams strips a leading '?' automatically,
 * but callers typically already strip it — both cases work fine.
 */
export function parse(search: string): Record<string, string | string[] | undefined> {
  const params = new URLSearchParams(search);
  const result: Record<string, string | string[] | undefined> = {};
  params.forEach((value, key) => {
    const existing = result[key];
    if (existing !== undefined) {
      if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        result[key] = [existing, value];
      }
    } else {
      result[key] = value;
    }
  });
  return result;
}

/**
 * Stringify an object into a query string (without leading '?').
 * Compatible with Node's `querystring.stringify()`.
 */
export function stringify(params: ParsedUrlQueryInput | Record<string, any>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  }
  return searchParams.toString();
}
